import { AffectedQuoteDto, AffectedQuotesResponse } from "api/tenant";
import { ExternalLink } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface AffectedQuotesConfirmContentProps {
  affectedQuotes: AffectedQuotesResponse;
  entityType: 'person' | 'realEstate' | 'tenant' | 'guarantor' | 'parc';
  onNavigate?: () => void;
}

const ITEMS_PER_PAGE = 10;

const getEntityLabel = (entityType: AffectedQuotesConfirmContentProps['entityType'], t: (key: string) => string): string => {
  switch (entityType) {
    case 'person':
      return t('AFFECTED_QUOTES.ENTITY_TYPES.PERSON');
    case 'realEstate':
      return t('AFFECTED_QUOTES.ENTITY_TYPES.REAL_ESTATE');
    case 'tenant':
      return t('AFFECTED_QUOTES.ENTITY_TYPES.TENANT');
    case 'guarantor':
      return t('AFFECTED_QUOTES.ENTITY_TYPES.GUARANTOR');
    case 'parc':
      return t('AFFECTED_QUOTES.ENTITY_TYPES.PARC');
    default:
      return '';
  }
};

const getQuoteUrl = (quote: AffectedQuoteDto): string | null => {
  if (!quote.riskId || !quote.quoteId) return null;

  // EMP branch uses borrowerProjectId route
  if (quote.branchCode === 'EMP' && quote.borrowerProjectId) {
    return `/simulation/contractualisation/borrower/${quote.borrowerProjectId}/${quote.riskId}/${quote.quoteId}`;
  }

  // Other branches use riskId/quoteId route
  return `/simulation/contractualisation/${quote.riskId}/${quote.quoteId}`;
};

export const AffectedQuotesConfirmContent: FC<AffectedQuotesConfirmContentProps> = ({
  affectedQuotes,
  entityType,
  onNavigate
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const entityLabel = getEntityLabel(entityType, t);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const allQuotes = affectedQuotes.quotes ?? [];
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const displayedQuotes = allQuotes.slice(0, displayedCount);
  const hasMore = displayedCount < allQuotes.length;

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayedCount(prev => Math.min(prev + ITEMS_PER_PAGE, allQuotes.length));
      setIsLoadingMore(false);
    }, 200);
  }, [isLoadingMore, hasMore, allQuotes.length]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 50;

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  const handleQuoteClick = useCallback((quote: AffectedQuoteDto) => {
    const url = getQuoteUrl(quote);
    if (url) {
      onNavigate?.();
      navigate(url);
    }
  }, [navigate, onNavigate]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <div className="space-y-4">
      <p className="text-gray-700">
        {t('AFFECTED_QUOTES.CONFIRMATION_MESSAGE', {
          count: affectedQuotes.count,
          entityType: entityLabel
        })}
      </p>

      {displayedQuotes.length > 0 && (
        <div
          ref={scrollContainerRef}
          className="max-h-60 overflow-y-auto border rounded-lg"
        >
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  {t('AFFECTED_QUOTES.TABLE.QUOTE_ID')}
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  {t('AFFECTED_QUOTES.TABLE.BRANCH')}
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  {t('AFFECTED_QUOTES.TABLE.SUBSCRIBER')}
                </th>
                <th className="px-4 py-2 text-left font-medium text-gray-600">
                  {t('AFFECTED_QUOTES.TABLE.STATUS')}
                </th>
                <th className="px-4 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedQuotes.map((quote: AffectedQuoteDto) => {
                const url = getQuoteUrl(quote);
                return (
                  <tr key={quote.quoteId} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {url ? (
                        <button
                          onClick={() => handleQuoteClick(quote)}
                          className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                        >
                          {quote.quoteIdentifier}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-900">
                          {quote.quoteIdentifier}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {quote.branchCode}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {quote.subscriberName}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                        {quote.statusLabel}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {url && (
                        <button
                          onClick={() => handleQuoteClick(quote)}
                          className="p-1 text-gray-400 hover:text-primary rounded transition-colors"
                          title={t('AFFECTED_QUOTES.VIEW_QUOTE')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {isLoadingMore && (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                      <span className="text-sm">Chargement...</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-gray-500">
        {t('AFFECTED_QUOTES.CONTINUE_QUESTION')}
      </p>
    </div>
  );
};
